import { IFText, IFButton, IFLoadingIndicator } from 'Components'
import PropTypes from 'prop-types'
import Styles from './OperatorUserListItem.module.css'
import styled from 'styled-components'
import Colors from 'Theme/Colors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import React, { useState, useImperativeHandle } from 'react'
import FormControl from '@mui/material/FormControl'
import { useTranslation } from 'react-i18next'
import { CheckPicker } from 'rsuite'
import { styled as muiStyled } from '@mui/system'

const PenIcon = styled.div`
  color: ${Colors.OperatorUserListPenIcon};
`

const DeleteIcon = styled.div`
  color: ${Colors.OperatorUserListDeleteIcon};
`

const OperatorUserListItem = React.forwardRef(
  (
    {
      operatorUser,
      avatar,
      permissions,
      pickedPermissions,
      isDeleteLoading,
      isUpdateLoading,
      onUpdateClick,
      onDeleteClick,
      editPermission,
    },
    ref,
  ) => {
    const { t } = useTranslation()

    const options = []
    for (const index in permissions) {
      options.push({
        label: permissions[index],
        value: permissions[index],
      })
    }

    const [accordExpanded, setAccordExpanded] = useState(false)
    const [selectedPermissions, setSelectedPermissions] =
      useState(pickedPermissions)
    const collapse = () => {
      setAccordExpanded(false)
    }

    useImperativeHandle(ref, () => ({
      collapse,
    }))

    const getPermissionPlaceholder = () => {
      let result = ''
      if (pickedPermissions[0] && pickedPermissions[1])
        result = `${pickedPermissions[0]} + ${pickedPermissions.length - 1}`
      else if (pickedPermissions[0]) result = pickedPermissions[0]
      else result = t('OperatorUserListItem.NoPermissionsSelected')
      return result
    }

    const onClickAccordionHandler = () => {
      setAccordExpanded((expanded) => {
        return !expanded
      })
    }

    const StyledAccordion = muiStyled(Accordion)({
      boxShadow: `0px 0px 0px 0px ${Colors.ChargingTransactionListItemShadow}`,
      paddingRight: '1rem',
      paddingLeft: '1rem',
      '&:hover': {
        backgroundColor: Colors.ChargePointLogListItemHover,
      },
    })
    const StyledFormControl = styled(FormControl)({
      height: '5rem',
      width: '12rem',
    })

    return (
      <div className={Styles.Container}>
        <StyledAccordion
          onClick={onClickAccordionHandler}
          expanded={accordExpanded && editPermission}
        >
          <AccordionSummary>
            <div className={Styles.ListItemSummary}>
              <div
                className={Styles.AvatarNameEmail}
                {...(!editPermission
                  ? {
                      style: {
                        flexGrow: 1,
                      },
                    }
                  : {})}
              >
                <div className={Styles.AvatarIcon}>{avatar}</div>
                <div className={Styles.NameEmail}>
                  <IFText className={Styles.Name}>{operatorUser.name}</IFText>
                  <IFText
                    className={Styles.Email}
                    style={{ color: Colors.OperatorUserListItemEmail }}
                  >
                    {operatorUser.email}
                  </IFText>
                </div>
              </div>

              {!accordExpanded || !editPermission ? (
                <div
                  className={Styles.PermissionsContainer}
                  {...(!editPermission
                    ? {
                        style: {
                          justifyContent: 'center',
                          flexGrow: 1,
                        },
                      }
                    : {})}
                >
                  <IFText
                    style={{ backgroundColor: Colors.GreyBackground }}
                    className={Styles.Role}
                  >
                    {getPermissionPlaceholder()}
                  </IFText>
                </div>
              ) : (
                <div className={Styles.CheckPicker}>
                  <StyledFormControl onClick={(e) => e.stopPropagation()}>
                    <CheckPicker
                      searchable={false}
                      preventOverflow={true}
                      data={options}
                      defaultValue={pickedPermissions}
                      menuStyle={{ color: Colors.text }}
                      onChange={(e) => setSelectedPermissions(e)}
                    />
                  </StyledFormControl>
                </div>
              )}

              {!accordExpanded ? (
                editPermission ? (
                  <PenIcon className={Styles.PenIcon}>
                    <FontAwesomeIcon icon={faPen} />
                  </PenIcon>
                ) : null
              ) : !editPermission ? null : !isDeleteLoading ? (
                <DeleteIcon className={Styles.DeleteIcon}>
                  <FontAwesomeIcon
                    icon={faTrash}
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteClick()
                    }}
                  />
                </DeleteIcon>
              ) : (
                <div className={Styles.LoadingIndicator}>
                  <IFLoadingIndicator size={'50%'} />
                </div>
              )}
            </div>
          </AccordionSummary>

          <AccordionDetails
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <div className={Styles.ListItemDetails}>
              <IFButton
                color={Colors.primary}
                text={t('OperatorUserListItem.cancelButton')}
                isLoading={false}
                size="xsm"
                isFill={false}
                className={Styles.CancelButton}
                onClick={onClickAccordionHandler}
              />

              <IFButton
                color={Colors.primary}
                text={t('OperatorUserListItem.updateButton')}
                isLoading={isUpdateLoading}
                size="xsm"
                isFill={true}
                className={Styles.UpdateButton}
                onClick={() => onUpdateClick(selectedPermissions)}
              />
            </div>
          </AccordionDetails>
        </StyledAccordion>
      </div>
    )
  },
)

OperatorUserListItem.propTypes = {
  operatorUser: PropTypes.object,
  avatar: PropTypes.object,
  permissions: PropTypes.arrayOf(PropTypes.string),
  pickedPermissions: PropTypes.arrayOf(PropTypes.string),
  onUpdateClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  isDeleteLoading: PropTypes.bool,
  isUpdateLoading: PropTypes.bool,
  editPermission: PropTypes.bool,
}

export default OperatorUserListItem
